/**
 * Per-candidate billing helpers for the employer app. Pure functions over the
 * `candidate-billing` API shape so the offline Node test can exercise them
 * without Vue or a browser. UK English, no hard-coded prices: every number
 * comes from the API or the organisation.
 */

export const PER_CANDIDATE = 'per_candidate'

/**
 * @param {Object|null} billing the API summary (model, band, currency, unit_amount, amounts, free_candidates, free_used, free_remaining, has_card, card, blocked, this_month, subscription)
 * @return {Boolean}
 */
export function isPerCandidate (billing) {
  return !!billing && billing.model === PER_CANDIDATE
}

/**
 * What the plan page should show for this organisation.
 *
 * @return {'free'|'needs_card'|'card_on_file'|'blocked'|'unknown'}
 */
export function billingState (billing) {
  if (!isPerCandidate(billing)) return 'unknown'
  if (billing.blocked) return 'blocked'
  if (billing.has_card) return 'card_on_file'
  if (Number(billing.free_remaining) > 0) return 'free'
  return 'needs_card'
}

/**
 * Whether a locked result can be opened by adding a card (as opposed to a
 * legacy plan). Used for the wording on locked results.
 */
export function unlockAction (billing) {
  const state = billingState(billing)
  if (state === 'blocked') return 'update_card'
  if (state === 'needs_card' || state === 'free') return 'upgrade'
  if (state === 'card_on_file') return 'none'
  return 'plan'
}

/**
 * Amount per candidate in minor units for a currency, from the band's
 * amounts, or null when the currency is not priced.
 */
export function unitAmountFor (billing, currency) {
  if (!billing || !billing.amounts || !currency) return null
  const amount = billing.amounts[String(currency).toUpperCase()]
  return Number.isInteger(amount) && amount > 0 ? amount : null
}

/**
 * Minor units to a currency string; whole amounts without pence.
 */
export function formatMinor (minor, currency, locale) {
  if (!Number.isFinite(minor) || !currency) return ''
  const amount = minor / 100
  try {
    return amount.toLocaleString(locale || 'en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2
    })
  } catch (e) {
    return currency + ' ' + amount
  }
}

/**
 * The one sentence the plan page leads with. Numbers come from the API.
 */
export function offerSentence (billing, currency) {
  const free = Number(billing && billing.free_candidates)
  const amount = unitAmountFor(billing, currency)
  if (!Number.isFinite(free) || amount === null) return ''
  const candidates = free === 1 ? 'candidate' : 'candidates'
  return `Results for your first ${free} ${candidates} are free. After that, ${formatMinor(amount, currency)} per candidate, billed monthly as completed results unlock automatically. No annual fee; stop future paid unlocking any time.`
}

/**
 * This month's line for the usage card.
 */
export function monthSummary (billing) {
  const month = billing && billing.this_month ? billing.this_month : null
  if (!month) return null
  const currency = month.currency || (billing && billing.currency)
  return {
    candidates: Number(month.candidates) || 0,
    free: Number(month.free_candidates) || 0,
    charged: Number(month.charged_candidates) || 0,
    amount: Number(month.amount) || 0,
    amountFormatted: formatMinor(Number(month.amount) || 0, currency),
    currency
  }
}

/**
 * Next invoice date from the subscription's period end (unix seconds), or null.
 */
export function nextInvoiceDate (billing) {
  const at = billing && billing.subscription ? billing.subscription.next_invoice_at : null
  if (!Number.isFinite(Number(at)) || Number(at) <= 0) return null
  return new Date(Number(at) * 1000)
}

/**
 * Parse Stripe's return to the card-complete page. Only a SetupIntent id and
 * its client secret are accepted; anything else is ignored.
 */
export function setupReturn (query) {
  if (!query) return null
  const id = query.setup_intent
  const secret = query.setup_intent_client_secret
  const status = query.redirect_status || null
  if (typeof id !== 'string' || !/^seti_[A-Za-z0-9]+$/.test(id)) return null
  if (typeof secret !== 'string' || !secret.startsWith(id + '_secret_')) return null
  return { setupIntentId: id, setupIntentClientSecret: secret, redirectStatus: status }
}

/**
 * The currency to offer on the card form: the organisation's currency once
 * set, otherwise the API's suggestion, otherwise the default.
 */
export function chooseCurrency (billing, suggestion, fallback) {
  const currencies = (billing && billing.currencies) || []
  if (billing && billing.has_card && currencies.includes(billing.currency)) return billing.currency
  if (suggestion && currencies.includes(String(suggestion).toUpperCase())) return String(suggestion).toUpperCase()
  if (fallback && currencies.includes(fallback)) return fallback
  return currencies[0] || 'USD'
}
